import React, { useContext } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import DayPicker from "react-day-picker";
import { PageHeader, InlineWaiting } from "components/ui";
import { connect } from "react-redux";
import { createWalkInOrder } from "action-creators";
import { PatientProfileContext } from "components/profile/context";
import { PRODUCT_LINE_ID } from "utils/walk-in-orders";

// Build { value: <product line id>, text: <name> } options from the patient's
// assigned product lines. A walk-in may only be recorded under a line the patient
// is actually assigned, so this returns an empty list (no fallback) when the
// patient has no assignable lines. Unknown line names (not in PRODUCT_LINE_ID)
// are dropped since the backend keys walk-ins by numeric id.
const buildProductLineChoices = patientProductLines =>
  (patientProductLines || [])
    .map(pl => ({ value: PRODUCT_LINE_ID[pl.text], text: pl.text }))
    .filter(pl => pl.value != null);

const WalkInOrder = ({
  patientId,
  userId,
  patient_product_lines,
  createWalkInOrder,
  closeSelf
}) => {
  const { fetchPatientWalkInOrdersAPI } = useContext(PatientProfileContext);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [selectedDay, setSelectedDay] = React.useState(new Date());
  const productLineChoices = React.useMemo(
    () => buildProductLineChoices(patient_product_lines),
    [patient_product_lines]
  );
  // Preselect when there's only one choice; otherwise require an explicit pick.
  const [productLine, setProductLine] = React.useState(
    productLineChoices.length === 1 ? String(productLineChoices[0].value) : ""
  );
  // patient_product_lines can resolve after the first render, so re-run the
  // single-choice preselect once the options settle rather than only at mount.
  React.useEffect(() => {
    if (!productLine && productLineChoices.length === 1) {
      setProductLine(String(productLineChoices[0].value));
    }
  }, [productLineChoices, productLine]);
  async function handleSubmit(e) {
    e.preventDefault();
    if (!productLine) return;
    try {
      setIsSubmitting(true);
      await createWalkInOrder(patientId, {
        order_date: selectedDay,
        user_id: userId,
        product_line: Number(productLine)
      });
      // Refresh the shared walk-in list so the tab reflects the new order
      // (replaces the old Redux invalidate/refetch coordination).
      fetchPatientWalkInOrdersAPI.fetchData(patientId);
      closeSelf();
    } finally {
      setIsSubmitting(false);
    }
  }
  return (
    <React.Fragment>
      <PageHeader title="New Walk-in Order" />
      <form onSubmit={handleSubmit}>
        <div className="row">
          <div className="small-12 columns">
            {productLineChoices.length === 0 ? (
              <p>
                This patient has no assignable product lines, so a walk-in order
                cannot be recorded.
              </p>
            ) : (
              <label>
                Product Line
                <select
                  value={productLine}
                  onChange={e => setProductLine(e.target.value)}
                  required
                >
                  <option value="">Select a product line...</option>
                  {productLineChoices.map(pl => (
                    <option key={pl.value} value={pl.value}>
                      {pl.text}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <div>
              {selectedDay
                ? moment(selectedDay).format("LL")
                : "Please select a day."}
            </div>
            <DayPicker
              onDayClick={day => setSelectedDay(day)}
              value={selectedDay}
              selectedDays={[selectedDay]}
            />
            <div
              style={{
                textAlign: "right"
              }}
            >
              {isSubmitting ? (
                <InlineWaiting />
              ) : (
                <button disabled={!productLine}>Add Walk-in Order</button>
              )}
            </div>
          </div>
        </div>
      </form>
    </React.Fragment>
  );
};

export default connect(
  state => ({
    userId: state.getIn(["user", "id"])
  }),
  { createWalkInOrder }
)(WalkInOrder);

WalkInOrder.propTypes = {
  createWalkInOrder: PropTypes.func.isRequired,
  userId: PropTypes.string.isRequired,
  patientId: PropTypes.string.isRequired,
  patient_product_lines: PropTypes.arrayOf(PropTypes.object),
  closeSelf: PropTypes.func.isRequired
};

WalkInOrder.defaultProps = {
  patient_product_lines: []
};
